/**
 * Format an amount with inline currency symbol.
 * EUR: "125.00 €" (suffix)
 * USD: "$125.00" (prefix)
 * Other currencies: uses Intl.NumberFormat defaults
 *
 * @param amount - Numeric amount (already in major units, e.g. 125.00 not cents)
 * @param currency - ISO 4217 currency code (e.g. "EUR", "USD")
 * @returns Formatted string with inline currency symbol
 */
export function formatAmountWithSymbol(
  amount: number,
  currency: string = "EUR",
): string {
  const formatter = new Intl.NumberFormat(getCurrencyLocale(currency), {
    style: "currency",
    currency: currency === null ? "EUR" : currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return formatter.format(amount);
}

/**
 * Format cents (minor units) to display string with currency symbol.
 *
 * @param cents - Amount in minor units (e.g. 12500 for 125.00)
 * @param currency - ISO 4217 currency code
 * @returns Formatted string with inline currency symbol
 */
export function formatCentsWithSymbol(
  cents: number,
  currency: string = "EUR",
): string {
  return formatAmountWithSymbol(cents / 100, currency);
}

function getCurrencyLocale(currency: string): string {
  switch (currency?.toUpperCase()) {
    case "EUR":
      return "fr-FR";
    case "USD":
      return "en-US";
    case "GBP":
      return "en-GB";
    case "JPY":
      return "ja-JP";
    case "CHF":
      return "de-CH";
    default:
      return "fr-FR";
  }
}

export default {
  formatAmountWithSymbol,
  formatCentsWithSymbol,
};
